import { acceptHMRUpdate, defineStore } from "pinia";
import { ref } from "vue";

import { Factory } from "@/data/dao/Factory";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { IBillEvent } from "@/data/model/Events";

import { logError } from "@/shared/logging/errors";

import { globalStores } from "@/app/globalStores";

// the articles the current user has ordered before - at the location for an end user, and for an operator the ones
// they ordered themselves on the open bill. the standard attribute "Ordered before" is linked to no article, it is
// derived from this set
export const useOrderedArticlesStore = defineStore("orderedArticles", () => {
    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);

    const mainArticleIds = ref(new Set<string>());
    // the options, by the top-level main item they were ordered in - the same option, e.g. a size, offered in another
    // item does not count there. see toOptionKey
    const optionKeys = ref(new Set<string>());
    // what the set is meant to hold, a response for anything else arrived too late and is dropped. the server finds the
    // bill of an operator itself, the table and the order bill are only here to tell when the set has to be asked for
    // again - switching tables, or opening or closing an order bill at the same table
    let current: { locationId: string; tableId: string | undefined; orderBillId: string | undefined } | undefined;
    let loadedKey: string | null = null;
    let pendingLoad: { key: string; fetchPromise: Promise<void> } | undefined;
    // a refresh keeps the key - after an order, a bill change or a login at the same table - so a response asked for
    // before it is told apart by this instead
    let generation = 0;

    // the bill of an operator also changes without them - it is paid and closed, or their items are moved or cancelled.
    // an end user goes by their own orders, which only they place, so only a table set listens.
    // App.vue subscribes it to the bill events of the app socket
    function onBillEvent(ev: IBillEvent) {
        if (current?.tableId == null || ev.locationId !== current.locationId || ev.tableId !== current.tableId) {
            return;
        }
        // without an order bill the server goes by the open bill of the table, which the event cannot tell apart from
        // an order bill at the same table, so any bill of the table counts
        if (current.orderBillId != null && ev.billId !== current.orderBillId) {
            return;
        }

        refresh().catch(logError);
    }

    async function load(locationId: string, tableId: string | undefined, orderBillId: string | undefined) {
        // a kiosk resets the session for every guest, so there is no previous order to go by
        if (globalStores.kioskStore?.isKioskMode === true) {
            return;
        }

        const key = toKey(locationId, tableId, orderBillId);
        if (loadedKey === key) {
            return;
        }

        // the set of another location, table or bill must not show until the new one arrives
        if (current == null || toKey(current.locationId, current.tableId, current.orderBillId) !== key) {
            mainArticleIds.value = new Set();
            optionKeys.value = new Set();
        }
        current = { locationId: locationId, tableId: tableId, orderBillId: orderBillId };

        if (pendingLoad?.key !== key) {
            pendingLoad = { key: key, fetchPromise: fetchOrdered(key, locationId) };
        }

        await pendingLoad.fetchPromise;
    }

    // called once an order was placed, the set stays as it is until the new one arrives. a request already on its way
    // was asked for before the change, so it is not waited for but replaced
    async function refresh() {
        if (current == null) {
            return;
        }

        generation++;
        pendingLoad = undefined;
        loadedKey = null;
        await load(current.locationId, current.tableId, current.orderBillId);
    }

    // called on a login or logout: the set of the previous identity must not show, and the allocation stays the same,
    // so nothing else asks for the new one. the table and the bill are passed in again, because the allocation was
    // reloaded while the previous identity still decided whether they count - a waiter logging in at a table becomes
    // an operator only now. without a location there is nothing to take over, the previous one is asked for again
    function reloadForNewIdentity(
        locationId: string | undefined,
        tableId: string | undefined,
        orderBillId: string | undefined
    ) {
        mainArticleIds.value = new Set();
        optionKeys.value = new Set();
        if (locationId != null) {
            current = { locationId: locationId, tableId: tableId, orderBillId: orderBillId };
        }

        refresh().catch(logError);
    }

    async function fetchOrdered(key: string, locationId: string) {
        const requestGeneration = generation;
        try {
            const ordered = await customerDao.getOrderedArticles(locationId);
            if (
                requestGeneration === generation &&
                current != null &&
                toKey(current.locationId, current.tableId, current.orderBillId) === key
            ) {
                mainArticleIds.value = new Set(ordered.mainArticleIds);
                optionKeys.value = new Set(ordered.options.map((c) => toOptionKey(c.mainArticleId, c.optionArticleId)));
                loadedKey = key;
            }
        } finally {
            if (requestGeneration === generation && pendingLoad?.key === key) {
                pendingLoad = undefined;
            }
        }
    }

    function toKey(locationId: string, tableId: string | undefined, orderBillId: string | undefined) {
        return `${locationId}|${tableId ?? ""}|${orderBillId ?? ""}`;
    }

    function toOptionKey(mainArticleId: string, optionArticleId: string) {
        return `${mainArticleId}|${optionArticleId}`;
    }

    // an option is passed with the top-level main item it is offered in, a main item without one
    function isOrdered(articleId: string | undefined, mainArticleId?: string): boolean {
        if (articleId == null) {
            return false;
        }

        return mainArticleId == null
            ? mainArticleIds.value.has(articleId)
            : optionKeys.value.has(toOptionKey(mainArticleId, articleId));
    }

    return {
        load,
        refresh,
        isOrdered,
        onBillEvent,
        reloadForNewIdentity,
    };
});

export type OrderedArticlesStore = ReturnType<typeof useOrderedArticlesStore>;

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useOrderedArticlesStore, import.meta.hot));
}
